import { Button, Flex, Modal, ScrollArea, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import CommentInput from "src/components/pages/Summary/CommentsModal/CommentInput";
import { usePostApi } from "src/hooks";
import { type IComment } from "src/pages/Summary/Summary.types";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setComments } from "src/redux/slices/verifySlice";

import ConfirmReviewCompletion from "./ConfirmReviewCompletion/ConfirmReviewCompletion";

interface Props {
  audit?: boolean;
  opened: boolean;
  onClose: () => void;
  comments?: IComment[] | null | undefined;
  refetchReviews: (body?: Record<string, any> | undefined) => Promise<void>;
  canCompleteReview: boolean;
}

const CompleteReview = ({
  audit,
  opened,
  onClose,
  refetchReviews,
  canCompleteReview,
}: Props) => {
  const isInitScroll = useRef(false);
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const [openedConfirmModal, { open, close }] = useDisclosure(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const comments = useSelector(state => state.verifySlice.comments);
  const { post, isLoading } = usePostApi();
  const dispatch = useDispatch();
  const { aid } = useParams();
  const [comment, setComment] = useState("");

  useEffect(() => {
    setTimeout(() => {
      if (
        opened &&
        scrollRef.current &&
        !isInitScroll.current &&
        comments &&
        comments.length > 0
      ) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "instant",
        });
        isInitScroll.current = true;
      }
    }, 100);
  }, [comments, opened]);

  const handleSetComments = useCallback(
    (res: IComment[]) => {
      dispatch(setComments(res));
    },
    [dispatch],
  );

  const handleSend = async (comment: string) => {
    if (comment)
      await post({
        request: "send-review-complete-comment",
        body: {
          params: {
            aid,
            message: comment,
          },
        },
        onSuccess: res => {
          handleSetComments(res.data.comments);
          setComment("");
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({
              top: scrollRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        },
      });
  };

  const handleClose = useCallback(() => {
    setComment("");
    onClose();
    isInitScroll.current = false;
  }, [onClose]);

  const onSuccess = useCallback(() => {
    handleClose();
    void refetchReviews();
  }, [handleClose, refetchReviews]);

  const handleCompleteReview = async () => {
    await handleSend(comment);
    open();
  };
  return (
    <>
      <ConfirmReviewCompletion
        opened={openedConfirmModal}
        close={close}
        onSuccess={onSuccess}
      />
      <Modal
        opened={opened}
        onClose={handleClose}
        title={audit ? "Complete Audit" : "Complete Review"}
        size="40vw">
        <ScrollArea
          viewportRef={scrollRef}
          my={20}
          type="auto"
          h="40vh"
          offsetScrollbars>
          <Flex direction="column" gap={12} px={12}>
            {comments?.map(comment => (
              <Flex
                key={`${comment.date}${comment.from}`}
                direction="column"
                p={10}
                bg="blue.9"
                gap={8}
                maw="70%"
                style={{
                  borderRadius: 12,
                }}>
                <Flex justify="space-between">
                  <Text size="xs" c="white">
                    {comment.from ?? `${comment.fname} ${comment.sname}`}
                  </Text>
                  <Text size="xs" c="white">
                    {dayjs(comment.date).format("D MMM YYYY - HH:mm")}
                  </Text>
                </Flex>
                <Text c="white" style={{ borderRadius: 12 }}>
                  {uppercase ? comment.message.toUpperCase() : comment.message}
                </Text>
              </Flex>
            ))}
          </Flex>
        </ScrollArea>

        {canCompleteReview && (
          <CommentInput
            onSend={handleSend}
            isLoading={isLoading}
            propsComment={comment}
            setPropsComment={setComment}
          />
        )}
        <Flex w={"100%"} justify={"end"} px={10} pt={16}>
          {canCompleteReview && (
            <Button
              onClick={handleCompleteReview}
              variant="outline"
              color="green"
              style={{ fontWeight: 200, borderWidth: 2 }}>
              {audit ? "Complete Audit" : "Complete Review"}
            </Button>
          )}
        </Flex>
      </Modal>
    </>
  );
};

export default CompleteReview;
